import DataLoader from 'dataloader';

import type { TokenAddressKey, TokenResult, TokensResponse } from '@/utils/0x/token-registry.types';
import { getFullURL, handleResponse } from '@/utils/fetcher';
import { tokenKey } from '@/utils/normalizeAddress';
import { routes } from '@/utils/routes';

/** Upstream token API caps a single address lookup at 100 tokens; stay well under. */
const TOKEN_ADDRESSES_LIMIT = 50;

/**
 * Normalized per-token React Query key. Using {@link tokenKey} (lowercased EVM
 * address + chainId) means the same token resolves to one cache entry regardless
 * of which hook requested it or how the address was cased. Tolerates missing
 * inputs (e.g. a disabled query) by emitting a stable non-matching key.
 */
export const tokenEntityQueryKey = (address: string | undefined, chainId: number | undefined) =>
  ['token', address && chainId ? tokenKey(address, chainId) : undefined] as const;

/**
 * Coalesces the per-token `fetchTokenByAddress` calls that fire when many token
 * queries mount together into batched `/api/tokens/by-address` requests. Each
 * token is sent as an `address:chainId` key, so a single request can span
 * multiple chains; requests are chunked at {@link TOKEN_ADDRESSES_LIMIT}.
 * Caching is left to React Query — the loader only batches, so there is no
 * second cache layer.
 */
const tokenLoader = new DataLoader<TokenAddressKey, TokenResult | undefined, string>(
  async (keys) => {
    const resolved = new Map<string, TokenResult>();
    const fetches: Promise<void>[] = [];
    for (let i = 0; i < keys.length; i += TOKEN_ADDRESSES_LIMIT) {
      const chunk = keys.slice(i, i + TOKEN_ADDRESSES_LIMIT);
      const params = new URLSearchParams({
        keys: chunk.map(({ address, chainId }) => tokenKey(address, chainId)).join(','),
      });
      const url = getFullURL(`${routes.api.TOKENS_BY_ADDRESS}?${params}`);
      fetches.push(
        fetch(url)
          .then(handleResponse<TokensResponse>)
          .then((response) => {
            if ('data' in response) {
              for (const token of response.data) {
                resolved.set(tokenKey(token.address, token.chainId), token);
              }
            }
          }),
      );
    }
    await Promise.all(fetches);

    return keys.map(({ address, chainId }) => resolved.get(tokenKey(address, chainId)));
  },
  { cache: false, cacheKeyFn: ({ address, chainId }) => tokenKey(address, chainId) },
);

/**
 * Resolve a single token's full {@link TokenResult} by address. Goes through
 * {@link tokenLoader} so concurrent lookups are batched. SSR callers must use
 * `fetchTokenByAddressServer` from `tokenEntityCache.server.ts` instead — this
 * module is part of the browser bundle and must not import the API route.
 */
export const fetchTokenByAddress = async (
  address: string,
  chainId: number,
): Promise<TokenResult | undefined> => tokenLoader.load({ address, chainId });

/**
 * Shared React Query options for a single token entity. Every token-by-address
 * hook composes this so reads dedupe on {@link tokenEntityQueryKey} and fetches
 * funnel through the batching {@link tokenLoader}.
 */
export const tokenEntityQueryOptions = (
  address: string | undefined,
  chainId: number | undefined,
) => ({
  queryKey: tokenEntityQueryKey(address, chainId),
  queryFn: async () => (await fetchTokenByAddress(address!, chainId!)) ?? null,
  enabled: !!address && !!chainId,
  staleTime: Infinity,
  refetchOnMount: false,
  refetchOnReconnect: false,
});
